/**
 * Professionals Management Page
 * Feature: 004-restore-missing-features
 * Task: T037, T038, T039, T042, T043, T044
 *
 * Admin page for managing organization professionals
 * Includes edit, deactivate, reactivate, remove actions, and search
 */

import { useState, useMemo } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Skeleton } from '@/components/ui/skeleton'
import { UserPlus, Users, Search, Copy, Check } from 'lucide-react'
import { MainLayout } from '@/components/layout'
import { useDocumentTitle } from '@/hooks/use-document-title'
import {
  useProfessionals,
  useDeactivateProfessional,
  useReactivateProfessional,
  useRemoveProfessional,
  useSendProfessionalResetEmail,
  useGenerateProfessionalTemporaryPassword,
} from '@/features/admin/hooks/use-professionals'
import { useProfessionalSearch } from '@/features/admin/hooks/use-professional-search'
import { ProfessionalCard } from '@/features/admin/components/ProfessionalCard'
import { ProfessionalSearchInput } from '@/features/admin/components/ProfessionalSearchInput'
import { CreateProfessionalModal } from '@/features/admin/components/CreateProfessionalModal'
import { EditProfessionalModal } from '@/features/admin/components/EditProfessionalModal'
import { RemoveProfessionalDialog } from '@/features/admin/components/RemoveProfessionalDialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/features/auth'
import { useToast } from '@/hooks/use-toast'

export const Route = createFileRoute('/_authenticated/admin/professionals')({
  component: ProfessionalsPage,
})

function ProfessionalsPage() {
  useDocumentTitle('Profissionais')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showRemoveDialog, setShowRemoveDialog] = useState(false)
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false)
  const [showResetEmailDialog, setShowResetEmailDialog] = useState(false)
  const [showTemporaryPasswordConfirmDialog, setShowTemporaryPasswordConfirmDialog] = useState(false)
  const [selectedProfessionalId, setSelectedProfessionalId] = useState<string | null>(null)
  const [temporaryPasswordData, setTemporaryPasswordData] = useState<{
    email: string
    password: string
    note: string
  } | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const { userProfile } = useAuth()
  const { toast } = useToast()

  const { data: professionals, isLoading } = useProfessionals()
  const deactivateMutation = useDeactivateProfessional()
  const reactivateMutation = useReactivateProfessional()
  const removeMutation = useRemoveProfessional()
  const sendResetEmailMutation = useSendProfessionalResetEmail()
  const generateTemporaryPasswordMutation = useGenerateProfessionalTemporaryPassword()

  // Filter professionals by search term
  const { filteredProfessionals, hasSearchTerm, resultCount } = useProfessionalSearch({
    professionals,
    searchTerm,
  })

  // Find selected professional for edit modal and remove dialog
  const selectedProfessional = useMemo(() => {
    if (!selectedProfessionalId || !professionals) return null
    const prof = professionals.find((p) => p.id === selectedProfessionalId)
    if (!prof) return null
    return {
      id: prof.id,
      name: prof.profiles?.full_name ?? 'Desconhecido',
      email: prof.profiles?.email ?? '',
      specialty: prof.specialty,
      role: prof.role as 'admin' | 'professional',
    }
  }, [selectedProfessionalId, professionals])

  // Edit handler
  const handleEdit = (id: string) => {
    setSelectedProfessionalId(id)
    setTimeout(() => setShowEditModal(true), 0)
  }

  // Deactivate handler - opens confirmation dialog
  const handleDeactivate = (id: string) => {
    setSelectedProfessionalId(id)
    setTimeout(() => setShowDeactivateDialog(true), 0)
  }

  // Confirm deactivate handler
  const handleConfirmDeactivate = async () => {
    if (!selectedProfessionalId) return

    try {
      await deactivateMutation.mutateAsync(selectedProfessionalId)
      toast({
        title: 'Profissional desativado',
        description: 'O profissional foi desativado com sucesso.',
      })
      setShowDeactivateDialog(false)
      setSelectedProfessionalId(null)
    } catch {
      toast({
        title: 'Erro',
        description: 'Não foi possível desativar o profissional.',
        variant: 'destructive',
      })
    }
  }

  // Reactivate handler
  const handleReactivate = async (id: string) => {
    try {
      await reactivateMutation.mutateAsync(id)
      toast({
        title: 'Profissional reativado',
        description: 'O profissional foi reativado com sucesso.',
      })
    } catch {
      toast({
        title: 'Erro',
        description: 'Não foi possível reativar o profissional.',
        variant: 'destructive',
      })
    }
  }

  // Remove handler - opens confirmation dialog
  const handleRemoveClick = (id: string) => {
    setSelectedProfessionalId(id)
    setTimeout(() => setShowRemoveDialog(true), 0)
  }

  const handleSendResetEmailClick = (id: string) => {
    setSelectedProfessionalId(id)
    setTimeout(() => setShowResetEmailDialog(true), 0)
  }

  const handleGenerateTemporaryPasswordClick = (id: string) => {
    setSelectedProfessionalId(id)
    setTimeout(() => setShowTemporaryPasswordConfirmDialog(true), 0)
  }

  // Confirm remove handler
  const handleConfirmRemove = async () => {
    if (!selectedProfessionalId) return

    try {
      await removeMutation.mutateAsync(selectedProfessionalId)
      toast({
        title: 'Profissional removido',
        description: 'O profissional foi removido da organização.',
      })
      setShowRemoveDialog(false)
      setSelectedProfessionalId(null)
    } catch {
      toast({
        title: 'Erro',
        description: 'Não foi possível remover o profissional.',
        variant: 'destructive',
      })
    }
  }

  const handleConfirmSendResetEmail = async () => {
    if (!selectedProfessionalId) return

    try {
      const result = await sendResetEmailMutation.mutateAsync(selectedProfessionalId)
      toast({
        title: 'Email enviado',
        description: `O link de redefinicao foi enviado para ${result.email}.`,
      })
      setShowResetEmailDialog(false)
      setSelectedProfessionalId(null)
    } catch (error) {
      toast({
        title: 'Erro',
        description: (error as Error).message ?? 'Não foi possível enviar o email de redefinição.',
        variant: 'destructive',
      })
    }
  }

  const handleConfirmGenerateTemporaryPassword = async () => {
    if (!selectedProfessionalId) return

    try {
      const result = await generateTemporaryPasswordMutation.mutateAsync(selectedProfessionalId)
      setTemporaryPasswordData({
        email: result.email,
        password: result.temporaryPassword,
        note: result.note,
      })
      setShowTemporaryPasswordConfirmDialog(false)
    } catch (error) {
      toast({
        title: 'Erro',
        description: (error as Error).message ?? 'Não foi possível gerar a senha temporária.',
        variant: 'destructive',
      })
    }
  }

  // Split filtered professionals by status
  const activeProfessionals = filteredProfessionals.filter((p) => p.is_active)
  const inactiveProfessionals = filteredProfessionals.filter((p) => !p.is_active)

  return (
    <MainLayout title="Profissionais" subtitle="Gerencie os profissionais da sua organização">
      <div className="space-y-6">
        {/* Header with search */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-50 rounded-lg">
                <UserPlus className="h-5 w-5 text-blue-600" />
              </div>
              <p className="text-sm text-gray-500">
                {professionals?.length ?? 0} profissionais
                {hasSearchTerm && ` (${resultCount} encontrados)`}
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 shadow-sm"
            >
              <UserPlus className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Novo Profissional</span>
              <span className="sm:hidden">Novo</span>
            </button>
          </div>
          {/* Search full-width below on all screens */}
          <ProfessionalSearchInput
            value={searchTerm}
            onChange={setSearchTerm}
          />
        </div>

        {isLoading ? (
          <ProfessionalsSkeleton />
        ) : professionals && professionals.length > 0 ? (
          // Has professionals - show filtered list or empty search result
          filteredProfessionals.length > 0 ? (
            <div className="space-y-6">
              {activeProfessionals.length > 0 && (
                <div>
                  <h2 className="text-lg font-medium text-gray-900 mb-4">
                    Ativos ({activeProfessionals.length})
                  </h2>
                  <div className="grid gap-4 md:grid-cols-2">
                    {activeProfessionals.map((professional) => (
                      <ProfessionalCard
                        key={professional.id}
                        professional={professional}
                        onEdit={handleEdit}
                        onSendResetEmail={handleSendResetEmailClick}
                        onGenerateTemporaryPassword={handleGenerateTemporaryPasswordClick}
                        onDeactivate={handleDeactivate}
                        onReactivate={handleReactivate}
                        onRemove={handleRemoveClick}
                        isSendingResetEmail={sendResetEmailMutation.isPending}
                        isGeneratingTemporaryPassword={generateTemporaryPasswordMutation.isPending}
                        currentUserId={userProfile?.id}
                      />
                    ))}
                  </div>
                </div>
              )}

              {inactiveProfessionals.length > 0 && (
                <div>
                  <h2 className="text-lg font-medium text-gray-500 mb-4">
                    Inativos ({inactiveProfessionals.length})
                  </h2>
                  <div className="grid gap-4 md:grid-cols-2">
                    {inactiveProfessionals.map((professional) => (
                      <ProfessionalCard
                        key={professional.id}
                        professional={professional}
                        onEdit={handleEdit}
                        onSendResetEmail={handleSendResetEmailClick}
                        onGenerateTemporaryPassword={handleGenerateTemporaryPasswordClick}
                        onReactivate={handleReactivate}
                        onRemove={handleRemoveClick}
                        isSendingResetEmail={sendResetEmailMutation.isPending}
                        isGeneratingTemporaryPassword={generateTemporaryPasswordMutation.isPending}
                        currentUserId={userProfile?.id}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Empty search result - T044
            <div className="card p-12 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mb-4">
                <Search className="h-6 w-6 text-gray-400" />
              </div>
              <p className="text-gray-600 font-medium">Nenhum profissional encontrado</p>
              <p className="text-sm text-gray-500 mt-1">
                Nenhum profissional corresponde a busca "{searchTerm}"
              </p>
              <button
                onClick={() => setSearchTerm('')}
                className="mt-4 text-sm text-teal-600 hover:text-teal-700 font-medium"
              >
                Limpar busca
              </button>
            </div>
          )
        ) : (
          // No professionals at all
          <div className="card p-12 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mb-4">
              <Users className="h-6 w-6 text-gray-400" />
            </div>
            <p className="text-gray-600 font-medium">Nenhum profissional encontrado</p>
            <p className="text-sm text-gray-500 mt-1">Adicione o primeiro profissional da organização</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 shadow-sm"
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Adicionar Profissional
            </button>
          </div>
        )}

        {/* Modals */}
        <CreateProfessionalModal
          open={showCreateModal}
          onOpenChange={setShowCreateModal}
        />

        <EditProfessionalModal
          open={showEditModal}
          onOpenChange={(open) => {
            setShowEditModal(open)
            if (!open) setSelectedProfessionalId(null)
          }}
          professional={selectedProfessional}
        />

        <RemoveProfessionalDialog
          open={showRemoveDialog}
          onOpenChange={(open) => {
            setShowRemoveDialog(open)
            if (!open) setSelectedProfessionalId(null)
          }}
          professionalName={selectedProfessional?.name ?? ''}
          onConfirm={handleConfirmRemove}
          isLoading={removeMutation.isPending}
        />

        <AlertDialog
          open={showDeactivateDialog}
          onOpenChange={(open) => {
            setShowDeactivateDialog(open)
            if (!open) setSelectedProfessionalId(null)
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Desativar profissional?</AlertDialogTitle>
              <AlertDialogDescription>
                O profissional{selectedProfessional?.name ? ` "${selectedProfessional.name}"` : ''}{' '}
                perderá acesso ao sistema. Você pode reativá-lo a qualquer momento.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deactivateMutation.isPending}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDeactivate}
                disabled={deactivateMutation.isPending}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {deactivateMutation.isPending ? 'Desativando...' : 'Desativar'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog
          open={showResetEmailDialog}
          onOpenChange={(open) => {
            if (sendResetEmailMutation.isPending) return
            setShowResetEmailDialog(open)
            if (!open) setSelectedProfessionalId(null)
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Enviar redefinicao por email?</AlertDialogTitle>
              <AlertDialogDescription>
                Vamos enviar um link de redefinicao para
                {selectedProfessional?.email ? ` ${selectedProfessional.email}` : ' este profissional'}.
                Ele podera criar uma nova senha no proprio fluxo seguro do sistema.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={sendResetEmailMutation.isPending}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={(event) => {
                  event.preventDefault()
                  void handleConfirmSendResetEmail()
                }}
                disabled={sendResetEmailMutation.isPending}
              >
                {sendResetEmailMutation.isPending ? 'Enviando...' : 'Enviar email'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog
          open={showTemporaryPasswordConfirmDialog}
          onOpenChange={(open) => {
            if (generateTemporaryPasswordMutation.isPending) return
            setShowTemporaryPasswordConfirmDialog(open)
            if (!open) setSelectedProfessionalId(null)
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Gerar senha temporaria?</AlertDialogTitle>
              <AlertDialogDescription>
                Isso vai substituir a senha atual
                {selectedProfessional?.name ? ` de ${selectedProfessional.name}` : ' do profissional'}.
                Compartilhe a nova senha apenas por um canal seguro e oriente a troca no primeiro login.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={generateTemporaryPasswordMutation.isPending}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={(event) => {
                  event.preventDefault()
                  void handleConfirmGenerateTemporaryPassword()
                }}
                disabled={generateTemporaryPasswordMutation.isPending}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {generateTemporaryPasswordMutation.isPending ? 'Gerando...' : 'Gerar senha'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <TemporaryPasswordDialog
          open={temporaryPasswordData !== null}
          onOpenChange={(open) => {
            if (!open) {
              setTemporaryPasswordData(null)
              setSelectedProfessionalId(null)
            }
          }}
          email={temporaryPasswordData?.email ?? ''}
          password={temporaryPasswordData?.password ?? ''}
          note={temporaryPasswordData?.note ?? ''}
        />
      </div>
    </MainLayout>
  )
}

function ProfessionalsSkeleton() {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="card p-4">
          <Skeleton className="h-32" />
        </div>
      ))}
    </div>
  )
}

function TemporaryPasswordDialog({
  open,
  onOpenChange,
  email,
  password,
  note,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  email: string
  password: string
  note: string
}) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(password)
    } catch {
      const textArea = document.createElement('textarea')
      textArea.value = password
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
    }

    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Senha temporaria gerada</DialogTitle>
          <DialogDescription>
            Compartilhe esta senha com cuidado. Ela substitui a senha anterior imediatamente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium text-foreground">Email</p>
            <p className="text-sm text-muted-foreground break-all">{email}</p>
          </div>

          <div>
            <p className="text-sm font-medium text-foreground">Senha temporaria</p>
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 rounded-md bg-muted px-3 py-2 font-mono text-sm break-all">
                {password}
              </code>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleCopy}
                title="Copiar senha"
              >
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {note}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
